import { useCallback, useEffect, useRef, useState } from 'react';
import { log } from '../lib/logger';

// kick-chat-service sends `event: heartbeat` every 20s; without one (or any message) for this long,
// treat the connection as dead
const HEARTBEAT_TIMEOUT_MS = 60000;
const RETRY_DELAYS_MS = [1000, 2000, 4000, 8000, 15000, 30000];
const MAX_RETRIES = 8;

/**
 * One SSE connection to kick-chat-service with heartbeat watchdog, exponential backoff and manual
 * reconnect. The connection only depends on `url`; handlers are read through a ref, so changing
 * them (e.g. when the viewer's moderator status loads) never tears the connection down.
 *
 * handlers: { onConnecting(), onHeartbeat(), onEvent(data) }
 * status:   'connecting' | 'connected' | 'reconnecting' | 'failed'
 */
export function useKickStream(url, handlers) {
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const [status, setStatus] = useState('connecting');
  const [connectAttempt, setConnectAttempt] = useState(0); // bumped by reconnect() to start over

  useEffect(() => {
    if (!url) return undefined;

    let source = null;
    let watchdog = null;
    let retryTimer = null;
    let retries = 0;
    let closed = false;

    const scheduleRetry = () => {
      if (closed) return;
      clearTimeout(watchdog);
      if (source) {
        source.close();
        source = null;
      }
      if (retries >= MAX_RETRIES) {
        setStatus('failed');
        return;
      }
      const delay = RETRY_DELAYS_MS[Math.min(retries, RETRY_DELAYS_MS.length - 1)];
      retries += 1;
      setStatus('reconnecting');
      retryTimer = setTimeout(connect, delay); // eslint-disable-line no-use-before-define
    };

    const armWatchdog = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(scheduleRetry, HEARTBEAT_TIMEOUT_MS);
    };

    const markAlive = () => {
      retries = 0;
      setStatus('connected');
      armWatchdog();
    };

    function connect() {
      if (closed) return;
      handlersRef.current.onConnecting?.();
      source = new EventSource(url);
      armWatchdog();

      source.addEventListener('connected', markAlive);
      source.addEventListener('heartbeat', () => {
        markAlive();
        handlersRef.current.onHeartbeat?.();
      });
      source.onmessage = (e) => {
        armWatchdog();
        let data;
        try {
          data = JSON.parse(e.data);
        } catch {
          console.error('[KickChat] Unparseable SSE message:', e.data);
          return;
        }
        log.debug('[KickChat] event:', data);
        handlersRef.current.onEvent?.(data);
      };
      // EventSource would retry on its own; take over so retries back off and eventually stop
      source.onerror = scheduleRetry;
    }

    setStatus('connecting');
    connect();

    return () => {
      closed = true;
      clearTimeout(watchdog);
      clearTimeout(retryTimer);
      if (source) source.close();
    };
  }, [url, connectAttempt]);

  const reconnect = useCallback(() => setConnectAttempt((n) => n + 1), []);

  return { status, reconnect };
}
